/**
 * Section components for the CategoricalExplainerHub.
 *
 * Each named export wraps one Section element with its full chrome
 * (eyebrow, title, description) and child explainer cards. They are
 * exported standalone so per-section tests can mount only the section
 * they care about and skip the 50+-card cost of the full hub.
 *
 * Every section is wrapped in `React.memo` because the hub composes them
 * once and the props never change — the memo cache means a parent re-render
 * does not recursively redo the section's reconciliation.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { Section } from "../../components/Section.js";
import {
  AdjunctionExplainer,
  AdjunctionsGenerateMonadsExplainer,
  BeckChevalleyExplainer,
  BialgebraExplainer,
  BimonadExplainer,
  BioperadExplainer,
  BisimulationExplainer,
  CompactClosedExplainer,
  ComonadExplainer,
  CooperadExplainer,
  DaggerCompactExplainer,
  DoubleCategoryExplainer,
  FAlgebraExplainer,
  FCoalgebraExplainer,
  FibrationExplainer,
  GradedMonadExplainer,
  HigherAdjunctionsExplainer,
  LinearCategoryExplainer,
  MonadExplainer,
  OperadExplainer,
  RecursionSchemesExplainer,
  ScopeEffectCoeffectExplainer,
  SliceCategoryExplainer,
  TriangleIdentitiesExplainer,
} from "../categorical/index.js";
import {
  AccountExplainer,
  AlwaysExplainer,
  ComposeExplainer,
  DataLineageExplainer,
  DataOriginExplainer,
  DataReachExplainer,
  DecomposeExplainer,
  DelegateExplainer,
  IcgAuthorityExplainer,
  IcgCompositionExplainer,
  IcgDataExplainer,
  IcgDomainExplainer,
  IcgResourceExplainer,
  IcgStateExplainer,
  IcgTemporalExplainer,
  InvConsistencyExplainer,
  InvEstablishExplainer,
  InvPreserveExplainer,
  PartitionExplainer,
  PermitExplainer,
  PrecommitExplainer,
  RequireExplainer,
  ResBalanceExplainer,
  ResConsumeExplainer,
  ResReleaseExplainer,
  SeparateExplainer,
  TraceExplainer,
} from "../ssm/index.js";
import { CategoricalImmunityExplainer, PurityExplainer } from "../master-theorems/index.js";

export interface HubSectionExplainerSpec {
  readonly anchorId: string;
  readonly Component: React.ComponentType;
}

export const MASTER_THEOREM_EXPLAINERS = [
  {
    anchorId: "explainer-categorical-immunity",
    Component: CategoricalImmunityExplainer,
  },
  { anchorId: "explainer-purity", Component: PurityExplainer },
] as const satisfies readonly HubSectionExplainerSpec[];

export const NEUTRALIZATION_THEOREM_EXPLAINERS = [
  { anchorId: "explainer-data-origin", Component: DataOriginExplainer },
  { anchorId: "explainer-data-reach", Component: DataReachExplainer },
  { anchorId: "explainer-permit", Component: PermitExplainer },
  { anchorId: "explainer-separate", Component: SeparateExplainer },
  { anchorId: "explainer-precommit", Component: PrecommitExplainer },
  { anchorId: "explainer-res-consume", Component: ResConsumeExplainer },
  { anchorId: "explainer-res-release", Component: ResReleaseExplainer },
  { anchorId: "explainer-compose", Component: ComposeExplainer },
] as const satisfies readonly HubSectionExplainerSpec[];

export const DUAL_PAIR_EXPLAINERS = [
  { anchorId: "explainer-data-lineage", Component: DataLineageExplainer },
  { anchorId: "explainer-res-balance", Component: ResBalanceExplainer },
] as const satisfies readonly HubSectionExplainerSpec[];

export const COMPOSITIONALITY_PRINCIPLE_EXPLAINERS = [
  { anchorId: "explainer-trace", Component: TraceExplainer },
  { anchorId: "explainer-account", Component: AccountExplainer },
  { anchorId: "explainer-require", Component: RequireExplainer },
  { anchorId: "explainer-delegate", Component: DelegateExplainer },
  { anchorId: "explainer-decompose", Component: DecomposeExplainer },
  { anchorId: "explainer-partition", Component: PartitionExplainer },
] as const satisfies readonly HubSectionExplainerSpec[];

export const IMMUNITY_CLOSURE_GUARANTEE_EXPLAINERS = [
  { anchorId: "explainer-icg-tr", Component: IcgDataExplainer },
  { anchorId: "explainer-icg-al", Component: IcgStateExplainer },
  { anchorId: "explainer-icg-st", Component: IcgResourceExplainer },
  { anchorId: "explainer-icg-at", Component: IcgTemporalExplainer },
  { anchorId: "explainer-icg-dt", Component: IcgAuthorityExplainer },
  { anchorId: "explainer-icg-de", Component: IcgCompositionExplainer },
  { anchorId: "explainer-icg-pa", Component: IcgDomainExplainer },
] as const satisfies readonly HubSectionExplainerSpec[];

function ExplainerList({
  items,
}: {
  readonly items: readonly HubSectionExplainerSpec[];
}): React.JSX.Element {
  return (
    <>
      {items.map(({ anchorId, Component }) => (
        <Component key={anchorId} />
      ))}
    </>
  );
}

export const MasterTheoremsSection = React.memo(
  function MasterTheoremsSection(): React.JSX.Element {
    return (
      <Section
        eyebrow="Master theorems"
        title="Tier 1: The Immunity"
        description="The Categorical Immunity Theorem is the shared root for the ten Neutralization Theorems; the Purity Invariant (code PURITY) certifies that no hidden generator is available outside the pure horizontal and vertical cell constructors."
      >
        <ExplainerList items={MASTER_THEOREM_EXPLAINERS} />
      </Section>
    );
  },
);

export const CategoricalBuildingBlocksSection = React.memo(
  function CategoricalBuildingBlocksSection(): React.JSX.Element {
    return (
      <Section
        eyebrow="Categorical structures"
        title="The building blocks"
        description="The structures CSK reuses across every Structural Safety Mechanism. F-algebras anchor 'stronger than initial state of a state machine'; coalgebras anchor 'every step preserves observable behavior'."
      >
        <FAlgebraExplainer />
        <FCoalgebraExplainer />
        <RecursionSchemesExplainer />
        <BisimulationExplainer />
        <BialgebraExplainer />
        <FibrationExplainer />
        <SliceCategoryExplainer />
        <DoubleCategoryExplainer />
      </Section>
    );
  },
);

export const AdjunctionFamilySection = React.memo(
  function AdjunctionFamilySection(): React.JSX.Element {
    return (
      <Section
        eyebrow="Categorical structures"
        title="The adjunction family — every Dual Pair, every Structural Duality Principle"
        description="Adjunctions are the universal vehicle for every Dual Pair Guarantee and every Structural Duality Principle in CSK. The pilot card defines F ⊣ G; the siblings expand the triangle identities, show how every adjunction generates a monad and a comonad, recast every CSK seam as a Beck-Chevalley failure, and lift the picture to higher and graded adjunctions. Scope · Effect · Coeffect names the trio every governed step lives in."
      >
        <AdjunctionExplainer />
        <TriangleIdentitiesExplainer />
        <AdjunctionsGenerateMonadsExplainer />
        <BeckChevalleyExplainer />
        <HigherAdjunctionsExplainer />
        <ScopeEffectCoeffectExplainer />
      </Section>
    );
  },
);

export const MonadFamilySection = React.memo(function MonadFamilySection(): React.JSX.Element {
  return (
    <Section
      eyebrow="Categorical structures"
      title="The monad family — commitments, requirements, grades"
      description="Monads and comonads describe how steps compose under an effect or a requirement. Their graded versions index every step by a grade drawn from a monoid — and the bimonad pairs them into one record. This is where PRECOMMIT and REQUIRE live."
    >
      <MonadExplainer />
      <ComonadExplainer />
      <GradedMonadExplainer />
      <BimonadExplainer />
    </Section>
  );
});

export const OperadFamilySection = React.memo(function OperadFamilySection(): React.JSX.Element {
  return (
    <Section
      eyebrow="Categorical structures"
      title="The operad family — composition, decomposition, round-trip safety"
      description="Operads are the structure of 'safe-in implies safe-out under composition.' Cooperads invert the arrows and make decomposition carry explicit residual dependencies. The bioperad pairs them on one carrier — this is where COMPOSE (C), DECOMPOSE (De), and the ICG-De Safety Envelope live."
    >
      <OperadExplainer />
      <CooperadExplainer />
      <BioperadExplainer />
    </Section>
  );
});

export const LinearCompactSection = React.memo(function LinearCompactSection(): React.JSX.Element {
  return (
    <Section
      eyebrow="Categorical structures"
      title="The linear / compact-closed family — resources, duals, reversibility"
      description="Linear categories enforce use-once discipline; compact-closed categories add duals and the evaluation/coevaluation maps that govern resource release. This is where RES.CONSUME (R1) and RES.RELEASE (R2) live. Dagger compact categories add coherent adjoints compatible with compact closure — a research direction for the ACCOUNT SDP."
    >
      <LinearCategoryExplainer />
      <CompactClosedExplainer />
      <DaggerCompactExplainer />
    </Section>
  );
});

export const NeutralizationTheoremsSection = React.memo(
  function NeutralizationTheoremsSection(): React.JSX.Element {
    return (
      <Section
        eyebrow="Structural Safety Mechanisms"
        title="The ten Neutralization Theorems (Stratum S1)"
        description="Each Neutralization Theorem is a projection of the Categorical Immunity Theorem onto one SecureGovernancePattern instance — not ten independent stories. Every card pairs its categorical structure with the seam it makes empty and the governance bridge that crosses it. The INV pair (I1/I2) is covered by the pilot cards below."
      >
        <ExplainerList items={NEUTRALIZATION_THEOREM_EXPLAINERS} />
      </Section>
    );
  },
);

export const DualPairGuaranteesSection = React.memo(
  function DualPairGuaranteesSection(): React.JSX.Element {
    return (
      <Section
        eyebrow="Structural Safety Mechanisms"
        title="The Dual Pair Guarantees (Stratum S1→S1)"
        description="Each Dual Pair Guarantee joins two Neutralization Theorems along an adjunction. Deploying both sides is strictly stronger than deploying either alone — the adjunction unit and counit close the gap each component leaves open. INV.CONSISTENCY (I1+I2) is covered by the pilot card below."
      >
        <ExplainerList items={DUAL_PAIR_EXPLAINERS} />
      </Section>
    );
  },
);

export const CompositionalityPrinciplesSection = React.memo(
  function CompositionalityPrinciplesSection(): React.JSX.Element {
    return (
      <Section
        eyebrow="Structural Safety Mechanisms"
        title="The Compositionality Principles (Stratum S2)"
        description="Each Structural Duality Principle certifies that a Neutralization Theorem or Dual Pair Guarantee composes safely under stacking. Level-1 SDPs come from 1-categorical adjunction pairs (the three DPGs); level-2 SDPs come from higher-categorical graded adjunctions for singleton NTs. ALWAYS (Al) is covered by the pilot card below."
      >
        <ExplainerList items={COMPOSITIONALITY_PRINCIPLE_EXPLAINERS} />
      </Section>
    );
  },
);

export const ImmunityClosureGuaranteesSection = React.memo(
  function ImmunityClosureGuaranteesSection(): React.JSX.Element {
    return (
      <Section
        eyebrow="Structural Safety Mechanisms"
        title="The Immunity Closure Guarantees (Stratum S1⊗S2)"
        description="Each Immunity Closure Guarantee — or Safety Envelope — pairs a Neutralization Theorem (or Dual Pair Guarantee) with its dual Structural Duality Principle, sealing the two into a single two-sided certificate. The adjunction unit and counit are the structural witness that the primal and dual sides are complementary, so the audit surface collapses from two checks to one envelope. Cards are named by domain (DATA, STATE, RESOURCE, TEMPORAL, AUTHORITY, COMPOSITION, DOMAIN); the canonical ICG codes (Tr, Al, St, At, Dt, De, Pa) remain the current normative names."
      >
        <ExplainerList items={IMMUNITY_CLOSURE_GUARANTEE_EXPLAINERS} />
      </Section>
    );
  },
);

export const PilotSsmSection = React.memo(function PilotSsmSection(): React.JSX.Element {
  return (
    <Section
      eyebrow="Structural Safety Mechanisms"
      title="The pilot SSMs (INV family + ALWAYS)"
      description="The two Neutralization Theorems on the F-algebra/F-coalgebra side, the Dual Pair Guarantee that joins them, and the Compositionality Principle that lets the joint envelope stack."
    >
      <InvEstablishExplainer />
      <InvPreserveExplainer />
      <InvConsistencyExplainer />
      <AlwaysExplainer />
    </Section>
  );
});
